import React, { Fragment } from 'react';
import Image from 'next/image';
import imageUrlBuilder from '@sanity/image-url';
import { PortableText, PortableTextComponents } from '@portabletext/react';
import { merge, cloneDeep } from 'lodash-es';
import { getImageDimensions } from '@sanity/asset-utils';
import { prodClient } from './lib/client';
import { cn, sanityImageUrl } from '@/lib/utils';
import quoteLightGreen from '@/public/global/quoteLightGreen.svg';
import RichTextCarousel from '@/components/global/richtext/RichTextCarousel';
import RichTextImage from '@/components/global/richtext/RichTextImage';
import { PRIZES } from '@/lib/prize-wheel/prizes';

const sanityImageBuilder = imageUrlBuilder(prodClient);
// { title: "Green background half", value: "greenBackgroundHalf" },
// { title: "Red background half", value: "redBackgroundHalf" },
// { title: "Grey background half", value: "greyBackgroundHalf" },
// { title: "Green Text", value: "greenText" },

export const richTextComponent: PortableTextComponents = {
  marks: {
    color: ({ children, value }) => (
      <span className="font-[inherit]" style={{ color: value?.hex }}>
        {children}
      </span>
    ),
    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
    greenSerifHighlight: ({ children }) => (
      <span className="font-bold text-[#6DA991] font-gestura">{children}</span>
    ),
    greenHighlight: ({ children }) => (
      <span
        style={{
          boxDecorationBreak: 'clone',
          WebkitBoxDecorationBreak: 'clone',
        }}
        className="bg-[#DCEEE5] text-black px-[0.2em]"
      >
        {children}
      </span>
    ),
    lightGreenHighlight: ({ children }) => (
      <span
        style={{
          background: 'linear-gradient(to top, #E2F3EF 55%, transparent 55%)',
          boxDecorationBreak: 'clone',
          WebkitBoxDecorationBreak: 'clone',
        }}
        className="text-[#003828]"
      >
        {children}
      </span>
    ),

    darkGreenHighlight: ({ children }) => (
      <span
        style={{
          background: 'linear-gradient(to top, #083628 55%, transparent 55%)',
          boxDecorationBreak: 'clone',
          WebkitBoxDecorationBreak: 'clone',
        }}
        className="text-[#D6EEE7]"
      >
        {children}
      </span>
    ),
    serif: ({ children }) => <span className="!font-blacker">{children}</span>,
    darkGreenSerif: ({ children }) => (
      <span className="!font-blacker font-normal italic text-[#003828]">
        {children}
      </span>
    ),
    lightGreenSerif: ({ children }) => (
      <span className="!font-blacker font-normal italic text-[#DDF2E9]">
        {children}
      </span>
    ),

    underline: ({ children }) => <u className="underline">{children}</u>,
    em: ({ children }) => (
      <em className="pr-[.125em] font-normal">{children}</em>
    ),
    inlineImage: ({ value }) => {
      const url = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : '';
      const dimensions = value?.image?.asset?._ref
        ? getImageDimensions(value?.image?.asset?._ref)
        : {
            width: 0,
            height: 0,
            aspectRatio: 0,
          };
      return (
        <Image
          className="inline-block mx-2 inline-icon"
          src={{
            src: url,
            width: dimensions.width,
            height: dimensions.height,
          }}
          alt="Icon image"
          quality={100}
        />
      );
    },
    inlineCornerChip: ({ value, children }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;
      const text = value?.text || '';

      return (
        <span className="rt-inlineCornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)]">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <span className="font-sans leading-none text-black text-[2rem] translate-y-[.125rem]">
            {text}
          </span>
        </span>
      );
    },
  },
  block: {
    h1: ({ children }) => <h1 className="">{children}</h1>,
    h2: ({ children }) => <h2 className="">{children}</h2>,
    h3: ({ children }) => <h3 className="">{children}</h3>,
    h4: ({ children }) => <h4 className="">{children}</h4>,
    h5: ({ children }) => <h5 className="">{children}</h5>,
    h6: ({ children }) => <h6 className="">{children}</h6>,
    normal: ({ children }) => <p className="p-normal">{children}</p>,
  },
  types: {
    image: ({ value }) => {
      const { width, height } = getImageDimensions(value);
      return (
        <RichTextImage
          src={sanityImageBuilder.image(value).url()}
          width={width}
          height={height}
          alt={value?.alt || ''}
        />
      );
    },
    imageCarousel: ({ value }) => {
      return <RichTextCarousel slides={value?.slides ?? []} />;
    },
    solidLine: ({ value }) => {
      const marginClass = value?.marginSize || 'my-[4rem]';

      return (
        <hr
          className={`w-full border-t border-white border-opacity-20 ${marginClass}`}
        />
      );
    },
    solidDecoratedLine: ({ value }) => {
      const marginClass = value?.marginSize || 'my-[5rem]';
      const bgColor = value?.color || '#153444';

      return (
        <div
          className={cn(
            'w-full relative flex items-center opacity-[.15]',
            marginClass,
          )}
        >
          <div
            className="flex-shrink-0 rotate-45 w-[.625rem] h-[.625rem]"
            style={{ backgroundColor: bgColor }}
          />
          <hr
            className="grow border-t !m-0"
            style={{
              borderColor: bgColor,
            }}
          />
          <div
            className="flex-shrink-0 rotate-45 w-[.625rem] h-[.625rem]"
            style={{ backgroundColor: bgColor }}
          />
        </div>
      );
    },
    iconItem: ({ value }) => {
      const imageUrl = value?.icon?.asset?._ref
        ? sanityImageUrl(value.icon.asset._ref)
        : null;
      return (
        <div className="flex items-start gap-[1.5rem] lg:gap-[2rem] my-[2rem] lg:my-[3rem]">
          {imageUrl && (
            <Image
              className="!w-[3rem] lg:!w-[3.8rem] !h-auto !m-0 flex-shrink-0 mt-[.25rem]"
              src={imageUrl}
              width={38}
              height={40}
              alt=""
            />
          )}
          <div className="flex flex-col gap-[1rem] flex-1">
            {value?.title && (
              <h3 className="text-[1.75rem] lg:text-[2.25rem] font-[600] leading-[1.3] m-0 text-[#1C1F22]">
                {value.title}
              </h3>
            )}
            {value?.text && (
              <div className="richtext richtext-start">
                <PortableText
                  value={value.text}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
        </div>
      );
    },
    dashedLine: ({ value }) => {
      const marginClass = value?.marginSize || 'my-[5rem]';

      return (
        <hr
          className={`w-full border-t border-dashed border-white border-opacity-20 ${marginClass}`}
        />
      );
    },
    cornerList: ({ value }) => {
      return (
        <ul className="flex flex-col items-start p-0 list-none cornerList mb-[1.25em] gap-[1rem]">
          {value?.items?.map((item: any, index: number) => {
            return (
              <li key={index} className="flex items-center p-0 m-0 gap-[1rem]">
                <div className="cornerListCorner bg-[#112628] flex-shrink-0 w-[1.5rem] h-[1.5rem] [clip-path:polygon(0_0,calc(100%-.75rem)_0,100%_.75rem,100%_100%,0_100%)]" />
                <div className="richtext richtext-start prose-p:leading-[1.1em]">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      );
    },
    cornerChip: ({ value }) => {
      const imageUrl = value?.image?.asset?._ref
        ? sanityImageUrl(value?.image?.asset?._ref)
        : null;
      const text = value?.text || '';

      return (
        <div className="rt-cornerChip inline-flex items-center gap-[1rem] min-h-[4rem] lg:min-h-[5rem] pl-[2rem] pr-[2.5rem] bg-[#D9F9B9] [clip-path:polygon(0_0,calc(100%-1.875rem)_0,100%_1.875rem,100%_100%,0_100%)] prose-p:text-black">
          {imageUrl && (
            <Image
              className="!w-[2rem] !h-auto !m-0"
              src={imageUrl}
              width={24}
              height={24}
              alt=""
            />
          )}
          <p className="mb-0 font-sans leading-none text-[2rem] translate-y-[.125rem]">
            {text}
          </p>
        </div>
      );
    },
    numberedList: ({ value }) => {
      const items = value?.items || [];
      const startNumber = value?.startNumber || 1;

      return (
        <ol
          className="flex flex-col items-start p-0 list-none numberedList mb-[1.25rem] gap-[1rem]"
          start={startNumber}
        >
          {items.map((item: any, index: number) => {
            return (
              <li
                key={index}
                className="flex items-center p-0 m-0 gap-[.625rem]"
              >
                <span className="flex-shrink-0 font-sans text-[#D9F9B9] min-w-[2rem]">
                  {startNumber + index}.
                </span>

                <div className="richtext richtext-start">
                  <PortableText
                    value={item.content}
                    components={richTextComponent}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      );
    },
    highlightBlock: ({ value }) => {
      return (
        <div className="flex relative items-start w-full bg-white ml-[0.5rem] highlightBlock mb-[3rem] px-[1.75rem] py-[1.12rem] before:absolute before:top-0 before:left-[-0.5rem] before:w-[0.25rem] before:h-full before:bg-[#71A961]">
          <div className="flex-1 richtext richtext-start">
            <PortableText value={value?.text} components={richTextComponent} />
          </div>
        </div>
      );
    },
    probabilityTable: () => (
      <div className="not-prose flex w-full flex-col">
        {PRIZES.map((prize, i) => (
          <div
            key={prize.id}
            className={cn(
              'flex items-center justify-between gap-[2rem] py-[1.25rem] first:pt-0',
              i < PRIZES.length - 1 && 'border-b border-black/[0.08]',
            )}
          >
            <span className="flex items-center gap-[1.25rem] text-[2rem] leading-[1.5] text-black/[0.6]">
              <svg
                className="h-[1.75rem] w-[1.75rem] shrink-0"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 8.5L6.2 11.5L13 4.5"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {prize.tableLabel}
            </span>
            <span className="text-[2rem] font-medium leading-[1.5] text-black [font-variant-numeric:tabular-nums]">
              {prize.weight}%
            </span>
          </div>
        ))}
      </div>
    ),
    grayBox: ({ value }) => {
      return (
        <div className="bg-[#f7f7f7] rounded-[1.5rem] p-[4.5rem] w-full richtext richtext-start prose-p:mb-0 prose-p:text-[2rem] prose-p:font-medium prose-p:text-black/80 prose-p:tracking-[-0.06rem] prose-p:leading-[1.7] prose-h3:mb-0 prose-h3:text-[3.5rem] prose-h3:font-medium prose-h3:text-[#2e3436] prose-h3:leading-[1.16] prose-h4:mb-0 prose-h4:text-[2.75rem] prose-h4:font-medium prose-h4:text-[#2e3436] prose-h4:leading-[1.16]">
          <PortableText value={value?.content} components={richTextComponent} />
        </div>
      );
    },
    quoteBlock: ({ value }) => {
      return (
        <div className="not-prose bg-[#0a241d] flex flex-col gap-[3rem] p-[5rem] lg:p-[5.75rem] rounded-[0.25rem] w-full">
          <div className="flex gap-[3rem] items-start w-full">
            <div className="rotate-180 flex-shrink-0">
              <svg
                className="w-[4rem] h-[3rem]"
                viewBox="0 0 32 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.3107 0V9.85467C13.3107 17.46 8.336 22.6147 1.33333 24L0.00666667 21.132C3.24933 19.9093 5.33333 16.2813 5.33333 13.3333H0V0H13.3107ZM32 0V9.85467C32 17.46 27.0027 22.616 20 24L18.672 21.132C21.916 19.9093 24 16.2813 24 13.3333H18.6893V0H32Z"
                  fill="#12F0B4"
                />
              </svg>
            </div>
            {value?.author && (
              <p className="mb-0 font-geist-mono font-semibold text-[1.75rem] leading-[1.33] text-[#12f0b4] opacity-[0.43] uppercase flex-1">
                {value.author}
              </p>
            )}
          </div>
          {value?.text && (
            <PortableText
              value={value.text}
              components={customRichTextComponent({
                block: {
                  normal: ({ children }) => (
                    <p className="mb-0 font-blacker text-[3rem] text-white leading-[1.4] tracking-[-0.09rem]">
                      {children}
                    </p>
                  ),
                },
              })}
            />
          )}
        </div>
      );
    },
    quoteBlockLight: ({ value }) => {
      return (
        <div className="flex flex-col items-start w-full lg:flex-row quoteBlockLight gap-[3rem] mb-[1rem] bg-[#1030370D] rounded-[1.5rem] px-[3.5rem] py-[3rem] lg:px-[6.25rem] lg:py-[2.5rem]">
          <div className="flex items-center gap-[3rem]">
            <Image
              className="!w-[2.75rem] !m-0 flex-shrink-0"
              src={quoteLightGreen}
              alt=""
            />
          </div>

          <div className="flex-1 richtext richtext-start tracking-[-0.075rem] lg:tracking-[-0.09rem] prose-p:!text-[2.5rem] lg:prose-p:!text-[3rem] prose-p:!font-[500] prose-p:!font-instrument-sans prose-p:!text-[#103037] prose-p:!text-opacity-100">
            <PortableText value={value?.text} components={richTextComponent} />
          </div>
        </div>
      );
    },
    greenBlock: ({ value }) => {
      return (
        <div
          className="flex flex-col items-start greenBlock mb-[1rem] bg-[#F5F8F6] rounded-[1.5rem] p-[4.5rem]
          [&>*:first-child]:mt-0 prose-p:text-[2rem]"
        >
          <PortableText value={value?.text} components={richTextComponent} />
        </div>
      );
    },
    whiteBlock: ({ value }) => {
      return (
        <div
          className="flex flex-col items-start greenBlock mb-[4rem] bg-white rounded-[2rem] p-[3rem]
          [&>*:first-child]:mt-0 [&>*:only-child]:mb-0"
        >
          <PortableText value={value?.text} components={richTextComponent} />
        </div>
      );
    },
    darkGreenBlock: ({ value }) => {
      return (
        <div
          className="flex flex-col items-start darkGreenBlock mb-[1rem] bg-[#18363C] rounded-[1.5rem] px-[3.5rem] lg:px-[4.5rem] py-[3rem]
          [&>*:first-child]:mt-0 prose-p:text-[2rem]"
        >
          <PortableText value={value?.text} components={richTextComponent} />
        </div>
      );
    },
    greenWithBorderBlock: ({ value }) => {
      return (
        <div
          className="flex flex-col items-start greenWithBorderBlock mb-[1rem] bg-[#6DA99124] border border-[#6DA991A6] rounded-[1.5rem] px-[3.5rem] lg:px-[4.5rem] py-[3rem]
          [&>*:first-child]:mt-0 prose-p:text-[2rem]"
        >
          <PortableText value={value?.text} components={richTextComponent} />
        </div>
      );
    },
    squareTopicListNoDivisor: ({ value }) => {
      const items = value?.items || [];

      return (
        <ul className="flex flex-col items-start p-0 list-none squareTopicListNoDivisor gap-[1.25rem]">
          {items.map((item: any, index: number) => {
            return (
              <Fragment key={index}>
                <li className="flex items-center p-0 m-0 gap-[1rem] topicItem">
                  <span className="bg-[#6DA991] w-[0.5rem] h-[0.5rem] shrink-0 topicItemSquare"></span>

                  <div className="richtext richtext-start prose-p:text-black prose-p:text-[2.25rem] prose-p:font-medium">
                    <PortableText
                      value={item.content}
                      components={richTextComponent}
                    />
                  </div>
                </li>
              </Fragment>
            );
          })}
        </ul>
      );
    },
  },
};

export const customRichTextComponent = (props: PortableTextComponents) => {
  return merge(cloneDeep(richTextComponent), props);
};
